/**
 * AskOlivia (H3b-6): "Ask Olivia…", a bar in the bottom corner of every signed-in screen with Olivia's round portrait, and a
 * panel with three tabs:
 *
 *   Ask            a question answered from Olivia's locked guide, with the parts it rests on ("From: …"), links to the
 *                  screens they name, and where the answer came from (a model, or the guide's own words and why);
 *   How it works   five diagrams drawn from data/guide-diagrams (FlowDiagram), the box of the current page marked "You are
 *                  here", each box opening a card with where it is and the rule cards that apply; the guide's text below,
 *                  folded (open when the diagrams cannot be read);
 *   Where do I…    every screen of the navigation the person may open, from nav/nav-config.ts, linked into the current client.
 *
 * The panel opens LARGE (the owner's word of 11 October, Valentin's `xl`: min(1100px, 96vw) × 88vh, the whole screen on a
 * phone), with a button to make it smaller and back, remembered in this browser only (every storage access in try/catch).
 * It is a modal dialog (HelpDialog: focus trap, Esc, focus back to the bar), its sections WAI-ARIA tabs (arrow keys, Home,
 * End). It reads /api/help/* only and never acts, changes or approves anything, and it reads no client's records.
 *
 * PROVENANCE: C:\Valentin\src\components\help\AskHelper.tsx @ d32c53d (main, H2/H3a), adapted 2026-10-11. CHANGES:
 * Olivia's dictionary, theme (ol-* tokens; the drawing's accent is Olivia's teal), routes and API helpers (useLoad, api,
 * ErrorText); "Where do I…" comes from the navigation instead of the pack's screen list; an answer set aside says why
 * (no cited part, a figure, an overstatement); the rule cards link to the Method page's cards.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BookOpen, Lock, MapPin, Maximize2, MessageCircleQuestion, Minimize2, ShieldAlert, Sparkles } from 'lucide-react'

import { useLanguage } from '../../i18n/LanguageProvider'
import type { MessageKey } from '../../i18n/en'
import { api } from '../../lib/api'
import type { FlowKind, FlowNode } from '../../lib/guide/flow-layout'
import { sheetIn } from '../../lib/guide/diagrams'
import {
  citationLabel, citationLine, currentBox, defaultDiagram, modelName, readPanelSize, routeHref, routeNeeds, whereGroups, writePanelSize,
  type GuideContent, type HelpAnswer, type HelpDiagramsAnswer, type HelpGuide, type PanelSize, type RouteNeed,
} from '../../lib/help'
import { methodLink } from '../../lib/method'
import { useSession } from '../../lib/session'
import { clientOf } from '../../lib/switcher'
import { useLoad } from '../../lib/useLoad'
import { ErrorText, Loading } from '../ui'
import FlowDiagram, { type FlowDiagramLabels } from './FlowDiagram'
import HelpDialog from './HelpDialog'

/** The portrait: the AI portal's picture of Olivia (C:\AI-Portal public/img/olivia/1-480.webp), cropped to the face, 96 px. */
export const AVATAR_SRC = '/helper/olivia-96.webp'
const QUESTION_MAX = 500
/** The portrait sits beside "Ask Olivia" in words: it is decorative, so its text alternative is empty (no text to translate). */
const DECORATIVE = ''

type Tab = 'ask' | 'how' | 'where'
const TABS: readonly Tab[] = ['ask', 'how', 'where']

// ── the bar ───────────────────────────────────────────────────────────────────────────────────────────────────────

export function AskOlivia() {
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [first, setFirst] = useState<string | null>(null)
  const inputId = useId()

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const q = draft.trim()
    setFirst(q === '' ? null : q)
    setDraft('')
    setOpen(true)
  }

  return (
    <>
      <form
        role="search"
        aria-label={t('help.title')}
        onSubmit={submit}
        className="fixed bottom-3 right-3 z-30 flex max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-full border border-ol-border bg-white p-1.5 shadow-lg print:hidden sm:bottom-4 sm:right-4"
      >
        <button
          type="button"
          onClick={() => {
            setFirst(null)
            setOpen(true)
          }}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex min-h-[44px] items-center gap-2 rounded-full pr-3 text-[15px] font-medium text-ol-text hover:bg-ol-page"
        >
          <img src={AVATAR_SRC} alt={DECORATIVE} width={36} height={36} className="h-9 w-9 shrink-0 rounded-full object-cover" />
          <span className="sm:sr-only">{t('help.bar')}</span>
        </button>
        <label htmlFor={inputId} className="sr-only">
          {t('help.bar.input')}
        </label>
        <input
          id={inputId}
          type="text"
          value={draft}
          maxLength={QUESTION_MAX}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('help.bar')}
          className="hidden w-56 rounded-full border border-ol-border px-3 py-2 text-[15px] text-ol-text placeholder:text-ol-text-3 sm:block lg:w-72"
        />
      </form>
      {open && (
        <HelpPanel
          initialQuestion={first}
          onClose={() => {
            setOpen(false)
            setFirst(null)
          }}
        />
      )}
    </>
  )
}

// ── the panel ─────────────────────────────────────────────────────────────────────────────────────────────────────

function contentOf(guide: HelpGuide | null, language: 'en' | 'sv'): GuideContent | null {
  if (!guide) return null
  return guide.guide.content[language] ?? guide.guide.content.en
}

export function HelpPanel({ initialQuestion, onClose }: { initialQuestion: string | null; onClose: () => void }) {
  const { t, language } = useLanguage()
  const guide = useLoad<HelpGuide>('help/guide')
  const [tab, setTab] = useState<Tab>('ask')
  const questionRef = useRef<HTMLTextAreaElement>(null)
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({})
  const base = useId()
  const content = contentOf(guide.data, language)
  const [size, setSize] = useState<PanelSize>(readPanelSize)
  const toggleSize = () => {
    const next: PanelSize = size === 'large' ? 'compact' : 'large'
    setSize(next)
    writePanelSize(next)
  }

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = TABS.indexOf(tab)
    const next =
      e.key === 'ArrowRight' ? TABS[(i + 1) % TABS.length] : e.key === 'ArrowLeft' ? TABS[(i + TABS.length - 1) % TABS.length] : e.key === 'Home' ? TABS[0] : e.key === 'End' ? TABS[TABS.length - 1] : undefined
    if (!next) return
    e.preventDefault()
    setTab(next)
    tabRefs.current[next]?.focus()
  }

  const label: Record<Tab, string> = { ask: t('help.tab.ask'), how: t('help.tab.how'), where: t('help.tab.where') }
  const icon: Record<Tab, ReactNode> = {
    ask: <MessageCircleQuestion size={16} aria-hidden />,
    how: <BookOpen size={16} aria-hidden />,
    where: <MapPin size={16} aria-hidden />,
  }

  return (
    <HelpDialog
      title={t('help.title')}
      subtitle={t('help.subtitle')}
      onClose={onClose}
      initialFocus={questionRef}
      size={size === 'large' ? 'xl' : 'md'}
      headerActions={
        <button
          type="button"
          onClick={toggleSize}
          className="hidden shrink-0 items-center gap-1.5 rounded-md border border-ol-border px-3 py-2 text-sm font-medium text-ol-text hover:bg-ol-page sm:inline-flex"
        >
          {size === 'large' ? <Minimize2 size={16} aria-hidden /> : <Maximize2 size={16} aria-hidden />}
          {size === 'large' ? t('help.size.toCompact') : t('help.size.toLarge')}
        </button>
      }
    >
      <div className="ol-help flex flex-col gap-4 text-[15px]">
        <div role="tablist" aria-label={t('help.tabs')} className="flex flex-wrap gap-1 border-b border-ol-border">
          {TABS.map((id) => (
            <button
              key={id}
              ref={(el) => {
                tabRefs.current[id] = el
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`${base}-panel-${id}`}
              tabIndex={tab === id ? 0 : -1}
              onClick={() => setTab(id)}
              onKeyDown={onTabKey}
              className={`-mb-px flex min-h-[44px] items-center gap-1.5 border-b-2 px-3 text-[15px] font-medium ${
                tab === id ? 'border-ol-action text-ol-action-dark' : 'border-transparent text-ol-text-2 hover:text-ol-text'
              }`}
            >
              {icon[id]}
              {label[id]}
            </button>
          ))}
        </div>
        {TABS.map((id) => (
          <div key={id} role="tabpanel" id={`${base}-panel-${id}`} aria-labelledby={`${base}-tab-${id}`} hidden={tab !== id} tabIndex={0} className="flex flex-col gap-3 outline-none">
            {id === 'ask' && <AskSection guide={guide.data} guideError={guide.error} content={content} questionRef={questionRef} initialQuestion={initialQuestion} onNavigate={onClose} />}
            {id === 'how' && <HowSection guide={guide.data} content={content} error={guide.error} onNavigate={onClose} />}
            {id === 'where' && <WhereSection onNavigate={onClose} />}
          </div>
        ))}
      </div>
    </HelpDialog>
  )
}

// ── Ask ───────────────────────────────────────────────────────────────────────────────────────────────────────────

function ProviderNotice({ guide }: { guide: HelpGuide | null }) {
  const { t } = useLanguage()
  if (!guide) return null
  const p = guide.provider
  return (
    <p className="flex items-start gap-1.5 text-ol-text-2">
      <Lock size={16} aria-hidden className="mt-1 shrink-0 text-ol-action" />
      <span>
        {p ? `${t('help.notice.ai', { model: modelName(p.model) })} ${p.engine === 'openrouter' ? t('help.notice.openrouter') : t('help.notice.anthropic')}` : t('help.notice.none')}
      </span>
    </p>
  )
}

function AskSection({
  guide,
  guideError,
  content,
  questionRef,
  initialQuestion,
  onNavigate,
}: {
  guide: HelpGuide | null
  guideError: unknown
  content: GuideContent | null
  questionRef: RefObject<HTMLTextAreaElement>
  initialQuestion: string | null
  onNavigate: () => void
}) {
  const { t, language } = useLanguage()
  const [question, setQuestion] = useState(initialQuestion ?? '')
  const [busy, setBusy] = useState(false)
  const [answer, setAnswer] = useState<HelpAnswer | null>(null)
  const [error, setError] = useState<unknown>(null)
  const fieldId = useId()
  const hintId = useId()
  const asked = useRef(false)

  const ask = useCallback(
    async (q: string) => {
      const text = q.trim()
      if (text === '') return
      setBusy(true)
      setError(null)
      setAnswer(null)
      try {
        setAnswer((await api.post<{ answer: HelpAnswer }>('help/ask', { question: text, language })).answer)
      } catch (err) {
        setError(err)
      } finally {
        setBusy(false)
      }
    },
    [language],
  )

  // A question typed into the bar is asked at once.
  useEffect(() => {
    if (initialQuestion && !asked.current) {
      asked.current = true
      void ask(initialQuestion)
    }
  }, [initialQuestion, ask])

  return (
    <>
      <div className="flex items-start gap-3">
        <img src={AVATAR_SRC} alt={DECORATIVE} width={48} height={48} className="h-12 w-12 shrink-0 rounded-full object-cover" />
        <p className="text-ol-text">{t('help.intro')}</p>
      </div>
      <ProviderNotice guide={guide} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void ask(question)
        }}
        className="flex flex-col gap-2"
      >
        <label htmlFor={fieldId} className="font-medium text-ol-text">
          {t('help.question')}
        </label>
        <textarea
          ref={questionRef}
          id={fieldId}
          value={question}
          maxLength={QUESTION_MAX}
          rows={3}
          aria-describedby={hintId}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void ask(question)
            }
          }}
          className="w-full rounded-md border border-ol-border px-3 py-2 text-[15px] text-ol-text"
        />
        <p id={hintId} className="text-sm text-ol-text-2">
          {t('help.questionHint')}
        </p>
        <div>
          <button type="submit" disabled={busy || question.trim() === ''} className="min-h-[44px] rounded-md bg-ol-action px-4 text-[15px] font-medium text-white hover:bg-ol-action-dark disabled:opacity-60">
            {t('help.send')}
          </button>
        </div>
      </form>
      <div aria-live="polite" className="flex flex-col gap-3">
        {busy && <p className="text-ol-text-2">{t('help.asking')}</p>}
        {error !== null && <ErrorText error={error} />}
        {error === null && !answer && guideError !== null && <ErrorText error={guideError} />}
        {answer && <AnswerView answer={answer} content={content} onNavigate={onNavigate} />}
      </div>
    </>
  )
}

const REASON_KEYS: Readonly<Record<string, MessageKey>> = {
  not_configured: 'help.reason.not_configured',
  rate_limited: 'help.reason.rate_limited',
  unavailable: 'help.reason.unavailable',
  spend_cap: 'help.reason.spend_cap',
  uncited: 'help.reason.uncited',
  figure: 'help.reason.figure',
  overstated: 'help.reason.overstated',
}

function AnswerView({ answer, content, onNavigate }: { answer: HelpAnswer; content: GuideContent | null; onNavigate: () => void }) {
  const { t } = useLanguage()
  const location = useLocation()
  if (answer.mode === 'refused') {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-ol-amber/40 bg-ol-amber-soft px-3 py-2 text-ol-amber" role="status">
        <ShieldAlert size={16} aria-hidden className="mt-1 shrink-0" />
        <span>{answer.reason === 'credentials' ? t('help.refused.credentials') : t('help.refused.personal_data')}</span>
      </p>
    )
  }
  if (answer.mode === 'none') return <p className="text-ol-text">{t('help.none')}</p>
  const from = content ? citationLine(t, content, answer.cited) : null
  const seeAlso = content ? answer.seeAlso.map((id) => citationLabel(t, content, id)).filter((x): x is string => x !== null) : []
  const links = content
    ? answer.screens
        .map((id) => {
          const s = content.screens.find((x) => x.id === id)
          return { id, name: s?.name ?? id, href: s?.path ? routeHref(s.path, location.pathname) : null }
        })
        .filter((l): l is { id: string; name: string; href: string } => l.href !== null)
    : []
  const reasonKey = answer.mode === 'keywords' && answer.reason ? REASON_KEYS[answer.reason] : undefined
  const heading = answer.mode === 'ai' ? t('help.answer.ai', { model: modelName(answer.model ?? '') }) : t('help.answer.keywords')
  return (
    <section aria-label={heading} className="flex flex-col gap-2 rounded-md border border-ol-border bg-ol-page px-3 py-3" data-mode={answer.mode}>
      <p className="flex items-center gap-1.5 font-medium text-ol-text">
        {answer.mode === 'ai' ? <Sparkles size={16} aria-hidden /> : <BookOpen size={16} aria-hidden />}
        {heading}
      </p>
      {reasonKey && <p className="text-ol-text-2">{t(reasonKey)}</p>}
      {answer.paragraphs.map((p, i) => (
        <p key={i} className="text-ol-text">
          {p}
        </p>
      ))}
      {from && <p className="text-sm text-ol-text-2">{from}</p>}
      {seeAlso.length > 0 && <p className="text-sm text-ol-text-2">{`${t('help.seeAlso')} ${seeAlso.join('; ')}`}</p>}
      {links.length > 0 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {links.map((l) => (
            <li key={l.id}>
              <Link to={l.href} onClick={onNavigate} className="text-ol-action-dark underline">
                {t('help.openScreen', { name: l.name })}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ── How it works ──────────────────────────────────────────────────────────────────────────────────────────────────

const KINDS: readonly FlowKind[] = ['step', 'decision', 'person', 'ai', 'rule', 'data', 'external', 'output']

/** The diagram's words, through t(). */
export function diagramLabels(t: (key: MessageKey, vars?: Record<string, string | number>) => string): FlowDiagramLabels {
  return {
    kinds: Object.fromEntries(KINDS.map((k) => [k, t(`help.diagram.kind.${k}` as MessageKey)])) as Record<FlowKind, string>,
    start: t('help.diagram.start'),
    prev: t('help.diagram.prev'),
    next: t('help.diagram.next'),
    position: (n, total, label) => t('help.diagram.position', { n, total, label }),
    count: (total) => t('help.diagram.count', { n: total }),
    choose: t('help.diagram.choose'),
    where: t('help.diagram.where'),
    rules: t('help.diagram.rules'),
    key: t('help.diagram.key'),
    list: t('help.diagram.list'),
    current: t('help.how.current'),
    badge: (n) => t('help.diagram.badge', { n }),
  }
}

const NEED_KEYS: Readonly<Record<RouteNeed, MessageKey>> = {
  client: 'help.needs.client',
  assessment: 'help.needs.assessment',
  practice: 'help.needs.practice',
  record: 'help.needs.record',
}

/** A route's name in the reader's words: the navigation's label for the screen it is, else the analysis's part, else the route. */
function routeName(t: (key: MessageKey, vars?: Record<string, string | number>) => string, route: string): string {
  const k = ROUTE_NAMES.find(([re]) => re.test(route))?.[1]
  return k ? t(k) : route
}

const ROUTE_NAMES: readonly (readonly [RegExp, MessageKey])[] = [
  [/^\/firm\/clients\/new$/, 'help.screen.newClient'],
  [/^\/firm\/ai-spend$/, 'nav.spend'],
  [/^\/portfolio\/inbox$/, 'nav.inbox'],
  [/^\/c\/:client\/onboarding$/, 'help.screen.onboarding'],
  [/^\/c\/:client\/discover$/, 'nav.discover'],
  [/^\/c\/:client\/assessments$/, 'nav.currentState'],
  [/^\/c\/:client\/assessments\/:assessment$/, 'help.screen.assessment'],
  [/\/assessments\/:assessment\/analysis$/, 'engine.nav.analysis'],
  [/\/assessments\/:assessment\/missing$/, 'engine.nav.missing'],
  [/\/assessments\/:assessment\/heatmap$/, 'engine.nav.heatmap'],
  [/\/assessments\/:assessment\/regulation$/, 'engine.nav.regulation'],
  [/\/assessments\/:assessment\/report$/, 'engine.nav.report'],
  [/\/assessments\/:assessment\/handover$/, 'engine.nav.handover'],
  [/\/assessments\/:assessment\/roadmap$/, 'engine.nav.roadmap'],
  [/\/assessments\/:assessment\/mitigation$/, 'engine.nav.mitigation'],
  [/\/assessments\/:assessment\/inbox$/, 'engine.nav.inbox'],
  [/\/assessments\/:assessment\/practices\/:practice$/, 'help.screen.practice'],
  [/\/assessments\/:assessment\/gaps\/:practice\/next$/, 'help.screen.nextSteps'],
  [/^\/c\/:client\/inquiries\/:inquiry$/, 'help.screen.interview'],
  [/^\/c\/:client\/requests\/:request$/, 'help.screen.request'],
  [/^\/c\/:client\/registers\/actions$/, 'nav.actions'],
  [/^\/c\/:client\/registers\/actions\/:action$/, 'help.screen.action'],
  [/^\/c\/:client\/documents$/, 'nav.documents'],
  [/^\/c\/:client\/cycle$/, 'nav.cycle'],
  [/^\/c\/:client\/progress$/, 'nav.progress'],
  [/^\/c\/:client\/members$/, 'nav.members'],
  [/^\/c\/:client\/modules$/, 'nav.modules'],
  [/^\/c\/:client\/ai$/, 'nav.aiDrafts'],
  [/^\/c\/:client\/handoffs$/, 'nav.handoffs'],
  [/^\/c\/:client\/handoffs\/:handoff$/, 'help.screen.handoff'],
]

function HowSection({ guide, content, error, onNavigate }: { guide: HelpGuide | null; content: GuideContent | null; error: unknown; onNavigate: () => void }) {
  const { t, language } = useLanguage()
  const location = useLocation()
  const diagrams = useLoad<HelpDiagramsAnswer>('help/diagrams')
  const all = useMemo(() => diagrams.data?.diagrams.diagrams ?? [], [diagrams.data])
  const [chosen, setChosen] = useState<string | null>(null)
  const pickId = useId()
  const active = chosen ?? defaultDiagram(all, location.pathname)
  const diagram = all.find((d) => d.id === active) ?? null
  const sheet = useMemo(() => (diagram ? sheetIn(diagram, language) : null), [diagram, language])
  const labels = useMemo(() => diagramLabels(t), [t])
  const current = diagram ? currentBox(diagram.nodes, diagram.steps, location.pathname) : null

  const renderWhere = (node: FlowNode) => {
    const routes = node.routes ?? []
    if (routes.length === 0) return <p className="text-ol-text-2">{t('help.diagram.noWhere')}</p>
    return (
      <ul className="flex flex-col gap-1">
        {routes.map((r) => {
          const name = routeName(t, r)
          const href = routeHref(r, location.pathname)
          const needs = href ? null : routeNeeds(r, location.pathname)
          return (
            <li key={r}>
              {href ? (
                <Link to={href} onClick={onNavigate} className="text-ol-action-dark underline">
                  {t('help.openScreen', { name })}
                </Link>
              ) : (
                <span className="font-medium text-ol-text">{name}</span>
              )}
              {needs && <span className="block text-sm text-ol-text-2">{t(NEED_KEYS[needs])}</span>}
            </li>
          )
        })}
      </ul>
    )
  }

  const renderRules = (node: FlowNode) => {
    const rules = node.rules ?? []
    if (rules.length === 0) return <p className="text-ol-text-2">{t('help.diagram.noRule')}</p>
    return (
      <ul className="flex flex-col gap-1">
        {rules.map((id) => (
          <li key={id}>
            <Link to={methodLink(id)} onClick={onNavigate} className="text-ol-action-dark underline">
              {diagrams.data?.rules[id]?.[language] ?? id}
            </Link>
            <span className="text-ol-text-2">{` (${id})`}</span>
          </li>
        ))}
      </ul>
    )
  }

  const failed = diagrams.error !== null
  return (
    <>
      {sheet && diagrams.data ? (
        <>
          <p className="text-ol-text">{t('help.how.diagramsIntro')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={pickId} className="font-medium text-ol-text">
              {t('help.how.pick')}
            </label>
            <select id={pickId} value={active ?? ''} onChange={(e) => setChosen(e.target.value)} className="min-h-[44px] rounded-md border border-ol-border bg-white px-3 text-[15px] text-ol-text">
              {all.map((d) => (
                <option key={d.id} value={d.id}>
                  {sheetIn(d, language).title}
                </option>
              ))}
            </select>
          </div>
          <p className="text-ol-text-2">{sheet.question}</p>
          {diagrams.data.diagrams.status !== 'approved' && <p className="text-sm text-ol-text-2">{t('help.draft', { version: diagrams.data.diagrams.version })}</p>}
          <FlowDiagram key={`${sheet.id}-${language}`} sheet={sheet} labels={labels} renderWhere={renderWhere} renderRules={renderRules} current={current} theme="light" />
        </>
      ) : diagrams.loading ? (
        <Loading />
      ) : null}
      {failed && (
        <>
          <p className="text-ol-text-2">{t('help.how.diagramsUnavailable')}</p>
          <ErrorText error={diagrams.error} />
        </>
      )}
      {!guide || !content ? (
        error !== null ? <ErrorText error={error} /> : <Loading />
      ) : (
        <details open={failed} className="rounded-md border border-ol-border px-3 py-2">
          <summary className="min-h-[44px] cursor-pointer py-2 font-semibold text-ol-text">{t('help.how.asText')}</summary>
          <div className="flex flex-col gap-3 pb-2">
            <GuideText guide={guide} content={content} />
          </div>
        </details>
      )}
    </>
  )
}

/** The guide's own text: its purpose, its steps and what AI does and never does. */
function GuideText({ guide, content }: { guide: HelpGuide; content: GuideContent }) {
  const { t } = useLanguage()
  return (
    <>
      {guide.guide.status !== 'approved' && <p className="text-sm text-ol-text-2">{t('help.draft', { version: guide.guide.version })}</p>}
      <p className="text-ol-text">{content.purpose}</p>
      <ol className="flex flex-col gap-2">
        {content.steps.map((s, i) => (
          <li key={s.id} className="rounded-md border border-ol-border px-3 py-2">
            <p className="font-semibold text-ol-text">{t('help.how.step', { n: i + 1, title: s.title })}</p>
            <dl className="mt-1 grid gap-1">
              <div>
                <dt className="inline font-medium text-ol-text">{`${t('help.how.youDo')}: `}</dt>
                <dd className="inline text-ol-text-2">{s.you_do}</dd>
              </div>
              <div>
                <dt className="inline font-medium text-ol-text">{`${t('help.how.itDoes')}: `}</dt>
                <dd className="inline text-ol-text-2">{s.it_does}</dd>
              </div>
              <div>
                <dt className="inline font-medium text-ol-text">{`${t('help.how.youDecide')}: `}</dt>
                <dd className="inline text-ol-text-2">{s.you_decide}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ol>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="font-semibold text-ol-text">{t('help.how.aiDoes')}</p>
          <ul className="ml-5 list-disc text-ol-text-2">
            {content.ai.does.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-semibold text-ol-text">{t('help.how.aiNever')}</p>
          <ul className="ml-5 list-disc text-ol-text-2">
            {content.ai.never.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  )
}

// ── Where do I… ───────────────────────────────────────────────────────────────────────────────────────────────────

const GROUP_KEYS: Readonly<Record<'portfolio' | 'client-work' | 'client-expert', MessageKey>> = {
  portfolio: 'help.where.portfolio',
  'client-work': 'help.where.clientWork',
  'client-expert': 'help.where.clientExpert',
}

function WhereSection({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useLanguage()
  const { person } = useSession()
  const location = useLocation()
  const client = clientOf(location.pathname)
  const groups = whereGroups(person.firmRoles, client)
  return (
    <>
      <p className="text-ol-text">{t('help.where.intro')}</p>
      {!client && <p className="text-ol-text-2">{t('help.where.noClient')}</p>}
      {groups.map((g) => (
        <section key={g.id} aria-labelledby={`where-${g.id}`} className="flex flex-col gap-2" data-where={g.id}>
          <h3 id={`where-${g.id}`} className="font-semibold text-ol-text">
            {t(GROUP_KEYS[g.id])}
          </h3>
          {g.sections.map(({ section, items }) => (
            <div key={`${g.id}-${section.id}`}>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-ol-text-3">{t(section.labelKey)}</h4>
              <ul className="flex flex-col gap-1">
                {items.map((w) => (
                  <li key={w.id} data-nav={w.id}>
                    {w.href ? (
                      <Link to={w.href} onClick={onNavigate} className="font-medium text-ol-action-dark underline">
                        {t(w.item.labelKey)}
                      </Link>
                    ) : (
                      <span className="font-medium text-ol-text">{t(w.item.labelKey)}</span>
                    )}
                    <span className="text-ol-text-2">{`: ${t(w.item.answersKey)}`}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </>
  )
}
